import { Injectable, signal, computed, inject } from '@angular/core';
import { Analysis, BusinessStatus } from '@shared/models/analysis.model';
import { AnalysisService } from '@shared/services/analysis.service';
import { CampaignAnalysesStats } from '@features/campaigns/models/campaign.model';

export interface AnalysisStoreState {
  analyses: Analysis[];
  selectedAnalyses: string[];
  loading: {
    isLoading: boolean;
    error: string | null;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  analysesStats: CampaignAnalysesStats | null;
}

export interface OperationResult {
  success: boolean;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AnalysisStore {
  private readonly analysisService = inject(AnalysisService);

  // Prefetch cache: store loaded pages to avoid re-fetching
  private prefetchCache = new Map<string, any>();
  private prefetchInProgress = new Set<string>();

  // Private signal for state management
  private readonly _state = signal<AnalysisStoreState>({
    analyses: [],
    selectedAnalyses: [],
    loading: { isLoading: false, error: null },
    pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    analysesStats: null,
  });

  // Pagination tracking for campaign context
  private currentCampaignId: string | null = null;
  private currentOrganizationId: string | null = null;

  // Public computed signals for reactive access
  readonly analyses = computed(() => this._state().analyses);
  readonly selectedAnalyses = computed(() => this._state().selectedAnalyses);
  readonly loading = computed(() => this._state().loading);
  readonly isLoading = computed(() => this._state().loading.isLoading);
  readonly hasError = computed(() => !!this._state().loading.error);
  readonly pagination = computed(() => this._state().pagination);
  readonly analysesStats = computed(() => this._state().analysesStats);

  // Computed selection state
  readonly selectedCount = computed(
    () => this._state().selectedAnalyses.length,
  );
  readonly allSelected = computed(() => {
    const analyses = this._state().analyses;
    const selected = this._state().selectedAnalyses;
    return analyses.length > 0 && selected.length === analyses.length;
  });

  /**
   * Load analyses for a specific campaign with pagination and prefetching
   */
  async loadAnalysesByCampaign(
    organizationId: string,
    campaignId: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<void> {
    // Track campaign context
    this.currentCampaignId = campaignId;
    this.currentOrganizationId = organizationId;

    const cacheKey = `${campaignId}:${page}:${limit}`;

    // Check if already in cache
    if (this.prefetchCache.has(cacheKey)) {
      const cachedResponse = this.prefetchCache.get(cacheKey);
      this._state.update((state) => ({
        ...state,
        analyses: cachedResponse.analyses,
        pagination: cachedResponse.pagination,
        analysesStats: cachedResponse.stats ? { queued: 0, ...cachedResponse.stats } : null,
        loading: { isLoading: false, error: null },
      }));
      console.log('[AnalysisStore] Loaded from cache:', cacheKey);
      // Trigger prefetch of adjacent pages
      this.prefetchAdjacentPages(organizationId, campaignId, page, limit, cachedResponse.pagination.totalPages);
      return;
    }

    // Set loading state
    this._state.update((state) => ({
      ...state,
      loading: { isLoading: true, error: null },
    }));

    try {
      console.log(
        '[AnalysisStore] Loading analyses for campaign:',
        campaignId,
        'page:',
        page,
      );
      const response = await this.analysisService.getAnalysesByCampaign(
        organizationId,
        campaignId,
        page,
        limit,
      );

      // Ensure analyses is always an array
      const safeAnalyses = Array.isArray(response.analyses)
        ? response.analyses
        : [];

      const pagination = response.pagination || {
        page,
        limit,
        total: response.total || 0,
        totalPages: Math.ceil((response.total || 0) / limit),
      };

      // Cache the response
      this.prefetchCache.set(cacheKey, { analyses: safeAnalyses, pagination, stats: response.stats });

      // Update state
      this._state.update((state) => ({
        ...state,
        analyses: safeAnalyses,
        pagination,
        analysesStats: response.stats ?? null,
        loading: { isLoading: false, error: null },
      }));

      console.log(
        '[AnalysisStore] Analyses loaded successfully:',
        safeAnalyses.length,
        'of',
        response.total,
      );

      // Trigger prefetch of adjacent pages in background
      this.prefetchAdjacentPages(organizationId, campaignId, page, limit, pagination.totalPages);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load analyses';
      console.error('[AnalysisStore] Error loading analyses:', error);

      // Single update for error state
      this._state.update((state) => ({
        ...state,
        loading: { isLoading: false, error: errorMessage },
      }));
    }
  }

  /**
   * Load every analysis of a campaign in one request.
   *
   * `totalPages: 0` puts DataTable into client-side mode, so search, column
   * filters, the entry limit and pagination all operate on the complete set.
   * With server-side paging the filters only ever saw the loaded page.
   */
  async loadAllAnalysesByCampaign(
    organizationId: string,
    campaignId: string,
  ): Promise<void> {
    this.currentCampaignId = campaignId;
    this.currentOrganizationId = organizationId;

    this._state.update((state) => ({
      ...state,
      loading: { isLoading: true, error: null },
    }));

    try {
      const { analyses, total, stats } =
        await this.analysisService.getAllAnalysesByCampaign(
          organizationId,
          campaignId,
        );
      const safeAnalyses = Array.isArray(analyses) ? analyses : [];

      this._state.update((state) => ({
        ...state,
        analyses: safeAnalyses,
        pagination: {
          page: 1,
          limit: safeAnalyses.length,
          total,
          totalPages: 0,
        },
        analysesStats: stats ?? null,
        loading: { isLoading: false, error: null },
      }));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load analyses';
      console.error('[AnalysisStore] Error loading all analyses:', error);
      this._state.update((state) => ({
        ...state,
        loading: { isLoading: false, error: errorMessage },
      }));
    }
  }

  /**
   * Prefetch adjacent pages (next and previous) in background
   */
  private prefetchAdjacentPages(
    organizationId: string,
    campaignId: string,
    currentPage: number,
    limit: number,
    totalPages: number,
  ): void {
    // Prefetch next page
    if (currentPage < totalPages) {
      const nextPageKey = `${campaignId}:${currentPage + 1}:${limit}`;
      if (!this.prefetchCache.has(nextPageKey) && !this.prefetchInProgress.has(nextPageKey)) {
        this.prefetchInProgress.add(nextPageKey);
        this.analysisService
          .getAnalysesByCampaign(organizationId, campaignId, currentPage + 1, limit)
          .then((response) => {
            const safeAnalyses = Array.isArray(response.analyses) ? response.analyses : [];
            const pagination = response.pagination || {
              page: currentPage + 1,
              limit,
              total: response.total || 0,
              totalPages: Math.ceil((response.total || 0) / limit),
            };
            this.prefetchCache.set(nextPageKey, { analyses: safeAnalyses, pagination });
            console.log('[AnalysisStore] Prefetched page:', currentPage + 1);
          })
          .catch((err) => {
            console.warn('[AnalysisStore] Prefetch failed for page', currentPage + 1, err);
          })
          .finally(() => this.prefetchInProgress.delete(nextPageKey));
      }
    }

    // Prefetch previous page
    if (currentPage > 1) {
      const prevPageKey = `${campaignId}:${currentPage - 1}:${limit}`;
      if (!this.prefetchCache.has(prevPageKey) && !this.prefetchInProgress.has(prevPageKey)) {
        this.prefetchInProgress.add(prevPageKey);
        this.analysisService
          .getAnalysesByCampaign(organizationId, campaignId, currentPage - 1, limit)
          .then((response) => {
            const safeAnalyses = Array.isArray(response.analyses) ? response.analyses : [];
            const pagination = response.pagination || {
              page: currentPage - 1,
              limit,
              total: response.total || 0,
              totalPages: Math.ceil((response.total || 0) / limit),
            };
            this.prefetchCache.set(prevPageKey, { analyses: safeAnalyses, pagination });
            console.log('[AnalysisStore] Prefetched page:', currentPage - 1);
          })
          .catch((err) => {
            console.warn('[AnalysisStore] Prefetch failed for page', currentPage - 1, err);
          })
          .finally(() => this.prefetchInProgress.delete(prevPageKey));
      }
    }
  }

  /**
   * Set current page and reload analyses
   */
  async setCurrentPage(page: number): Promise<void> {
    if (!this.currentCampaignId || !this.currentOrganizationId) {
      console.warn('[AnalysisStore] Cannot change page without campaign context');
      return;
    }

    const currentPagination = this._state().pagination;
    await this.loadAnalysesByCampaign(
      this.currentOrganizationId,
      this.currentCampaignId,
      page,
      currentPagination.limit,
    );
  }

  /**
   * Load analyses for an organization (all analyses, not campaign-specific)
   */
  async loadAnalysesByOrganization(organizationId: string): Promise<void> {
    // Set loading immediately with single update
    this._state.update((state) => ({
      ...state,
      loading: { isLoading: true, error: null },
    }));

    try {
      console.log(
        '[AnalysisStore] Loading analyses for organization:',
        organizationId,
      );
      const analyses =
        await this.analysisService.getAnalysesByOrganization(organizationId);

      // Ensure analyses is always an array
      const safeAnalyses = Array.isArray(analyses) ? analyses : [];

      // Single atomic update with result and completion
      this._state.update((state) => ({
        ...state,
        analyses: safeAnalyses,
        loading: { isLoading: false, error: null },
      }));

      console.log(
        '[AnalysisStore] Analyses loaded successfully:',
        safeAnalyses.length,
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load analyses';
      console.error('[AnalysisStore] Error loading analyses:', error);

      // Single update for error state
      this._state.update((state) => ({
        ...state,
        loading: { isLoading: false, error: errorMessage },
      }));
    }
  }

  /**
   * Create a new analysis
   * @param organizationId - The organization ID
   * @param analysisData - The analysis data
   * @param campaignId - Optional campaign ID. If provided, creates analysis under campaign
   */
  async createAnalysis(
    organizationId: string,
    analysisData: any,
    campaignId?: string,
  ): Promise<Analysis | null> {
    this.setLoading(true, null);

    try {
      console.log('[AnalysisStore] Creating analysis:', analysisData);
      const newAnalysis = await this.analysisService.createAnalysis(
        organizationId,
        analysisData,
        campaignId,
      );

      // For standalone analyses (no campaign), trigger geocut via the execute
      // endpoint so technicalTask/State are set before the detail page loads,
      // allowing SSE to auto-connect and the timeline to show the correct state.
      // `skipAutoStart` opts out: the caller dispatches its own task window right
      // after this returns, and two starts on one analysis collide.
      if (!campaignId && !analysisData?.skipAutoStart) {
        try {
          await this.analysisService.executeAnalysisTasks(
            organizationId,
            newAnalysis.analysisId,
            { start_task: 1, end_task: 1 },
          );
        } catch (executeError) {
          console.error(
            '[AnalysisStore] Failed to auto-start geocut after creation:',
            executeError,
          );
        }
      }

      // Add to local state
      this._state.update((state) => ({
        ...state,
        analyses: [...state.analyses, newAnalysis],
        loading: { isLoading: false, error: null },
      }));

      console.log(
        '[AnalysisStore] Analysis created successfully:',
        newAnalysis,
      );
      return newAnalysis;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create analysis';
      console.error('[AnalysisStore] Error creating analysis:', error);

      this.setLoading(false, errorMessage);
      return null;
    }
  }

  /**
   * Delete an analysis
   * @param organizationId - The organization ID
   * @param analysisId - The analysis ID
   * @returns OperationResult with success status and optional error message
   */
  async deleteAnalysis(
    organizationId: string,
    analysisId: string,
  ): Promise<OperationResult> {
    try {
      console.log('[AnalysisStore] Deleting analysis:', analysisId);
      await this.analysisService.deleteAnalysis(organizationId, analysisId);

      // Remove from local state
      this._state.update((state) => ({
        ...state,
        analyses: state.analyses.filter((a) => a.analysisId !== analysisId),
        selectedAnalyses: state.selectedAnalyses.filter(
          (id) => id !== analysisId,
        ),
      }));

      console.log('[AnalysisStore] Analysis deleted successfully');
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete analysis';
      console.error('[AnalysisStore] Error deleting analysis:', error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Cancel an analysis
   * @param organizationId - The organization ID
   * @param analysisId - The analysis ID
   * @returns OperationResult with success status and optional error message
   */
  async cancelAnalysis(
    organizationId: string,
    analysisId: string,
  ): Promise<OperationResult> {
    try {
      console.log('[AnalysisStore] Cancelling analysis:', analysisId);
      const updatedAnalysis = await this.analysisService.cancelAnalysis(
        organizationId,
        analysisId,
      );

      // Update in local state
      this._state.update((state) => ({
        ...state,
        analyses: state.analyses.map((a) =>
          a.analysisId === analysisId ? updatedAnalysis : a,
        ),
      }));

      console.log('[AnalysisStore] Analysis cancelled successfully');
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to cancel analysis';
      console.error('[AnalysisStore] Error cancelling analysis:', error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Update analysis status
   * @param organizationId - The organization ID
   * @param analysisId - The analysis ID
   * @param status - The new status
   */
  async updateStatus(
    organizationId: string,
    analysisId: string,
    status: BusinessStatus,
  ): Promise<OperationResult> {
    try {
      console.log(
        '[AnalysisStore] Updating analysis status:',
        analysisId,
        status,
      );
      const updatedAnalysis = await this.analysisService.updateAnalysisStatus(
        organizationId,
        analysisId,
        status,
      );

      // Update in local state
      this._state.update((state) => ({
        ...state,
        analyses: state.analyses.map((a) =>
          a.analysisId === analysisId ? { ...a, status } : a,
        ),
      }));

      console.log('[AnalysisStore] Analysis status updated successfully');
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update analysis status';
      console.error('[AnalysisStore] Error updating analysis status:', error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Update analysis properties (e.g. analysisType)
   * @param organizationId - The organization ID
   * @param analysisId - The analysis ID
   * @param data - The data to update
   */
  async updateAnalysis(
    organizationId: string,
    analysisId: string,
    data: Partial<Analysis>,
  ): Promise<OperationResult> {
    try {
      console.log('[AnalysisStore] Updating analysis:', analysisId, data);
      const updatedAnalysis = await this.analysisService.updateAnalysis(
        organizationId,
        analysisId,
        data,
      );

      // Update in local state - merge only the updated fields
      this._state.update((state) => ({
        ...state,
        analyses: state.analyses.map((a) =>
          a.analysisId === analysisId ? { ...a, ...data } : a,
        ),
      }));

      console.log('[AnalysisStore] Analysis updated successfully');
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update analysis';
      console.error('[AnalysisStore] Error updating analysis:', error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Archive an analysis
   * @param organizationId - The organization ID
   * @param analysisId - The analysis ID
   */
  async archiveAnalysis(
    organizationId: string,
    analysisId: string,
  ): Promise<OperationResult> {
    try {
      console.log('[AnalysisStore] Archiving analysis:', analysisId);
      const updatedAnalysis = await this.analysisService.archiveAnalysis(
        organizationId,
        analysisId,
      );

      // Update in local state
      this._state.update((state) => ({
        ...state,
        analyses: state.analyses.map((a) =>
          a.analysisId === analysisId ? updatedAnalysis : a,
        ),
      }));

      console.log('[AnalysisStore] Analysis archived successfully');
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to archive analysis';
      console.error('[AnalysisStore] Error archiving analysis:', error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Unarchive an analysis
   * @param organizationId - The organization ID
   * @param analysisId - The analysis ID
   */
  async unarchiveAnalysis(
    organizationId: string,
    analysisId: string,
  ): Promise<OperationResult> {
    try {
      console.log('[AnalysisStore] Unarchiving analysis:', analysisId);
      const updatedAnalysis = await this.analysisService.unarchiveAnalysis(
        organizationId,
        analysisId,
      );

      // Update in local state
      this._state.update((state) => ({
        ...state,
        analyses: state.analyses.map((a) =>
          a.analysisId === analysisId ? updatedAnalysis : a,
        ),
      }));

      console.log('[AnalysisStore] Analysis unarchived successfully');
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to unarchive analysis';
      console.error('[AnalysisStore] Error unarchiving analysis:', error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Toggle selection of an analysis
   */
  toggleSelection(analysisId: string): void {
    this._state.update((state) => {
      const selected = state.selectedAnalyses;
      const newSelected = selected.includes(analysisId)
        ? selected.filter((id) => id !== analysisId)
        : [...selected, analysisId];

      return {
        ...state,
        selectedAnalyses: newSelected,
      };
    });
  }

  /**
   * Select all analyses
   */
  selectAll(): void {
    this._state.update((state) => ({
      ...state,
      selectedAnalyses: state.analyses.map((a) => a.analysisId),
    }));
  }

  /**
   * Clear all selections
   */
  clearSelection(): void {
    this._state.update((state) => ({
      ...state,
      selectedAnalyses: [],
    }));
  }

  /**
   * Set selection
   */
  setSelection(selectedIds: string[]): void {
    this._state.update((state) => ({
      ...state,
      selectedAnalyses: selectedIds,
    }));
  }

  /**
   * Silently re-fetch the whole campaign's rows + stats WITHOUT toggling the
   * loading flag. Because it never sets `isLoading`, the table shows no spinner
   * and — since the DataTable paginates the data client-side — the user's current
   * page is preserved (replacing the data array doesn't reset the page). Used to
   * refresh technical states during a run without disrupting the table.
   */
  async refreshStats(): Promise<void> {
    const orgId = this.currentOrganizationId;
    const campaignId = this.currentCampaignId;
    if (!orgId || !campaignId) return;

    try {
      const { analyses, total, stats } =
        await this.analysisService.getAllAnalysesByCampaign(orgId, campaignId);
      const safe = Array.isArray(analyses) ? analyses : [];
      this._state.update((state) => ({
        ...state,
        analyses: safe,
        analysesStats: stats ?? state.analysesStats,
        pagination: { ...state.pagination, total },
      }));
    } catch {
      // Background refresh — stay silent on transient failures.
    }
  }

  /**
   * Merge a partial update into one analysis's local state, no network call.
   * Use when a mutation endpoint already returns the updated row (e.g. a
   * retry response) — patching locally avoids a full org-wide reload just to
   * reflect one row's new state.
   */
  patchAnalysisLocally(analysisId: string, patch: Partial<Analysis>): void {
    this._state.update((state) => ({
      ...state,
      analyses: state.analyses.map((a) =>
        a.analysisId === analysisId ? { ...a, ...patch } : a,
      ),
    }));
  }

  patchAnalysesLocally(
    updates: Array<{ id: string; patch: Partial<Analysis> }>,
  ): void {
    if (!updates.length) return;
    const patches = new Map(updates.map((update) => [update.id, update.patch]));
    this._state.update((state) => ({
      ...state,
      analyses: state.analyses.map((item) => {
        const patch = patches.get(item.id ?? item.analysisId);
        return patch ? { ...item, ...patch } : item;
      }),
    }));
    this.clearCache();
  }

  /**
   * Clear the prefetch cache so the next load fetches fresh data from the API.
   * Use after mutating operations (e.g. bulk pipeline trigger) to ensure
   * technical states are refreshed.
   */
  clearCache(): void {
    this.prefetchCache.clear();
    this.prefetchInProgress.clear();
  }

  /**
   * Clear all analyses
   */
  clearAnalyses(): void {
    this._state.update((state) => ({
      ...state,
      analyses: [],
      selectedAnalyses: [],
      loading: { isLoading: false, error: null },
    }));
  }

  /**
   * Set loading state
   */
  private setLoading(isLoading: boolean, error: string | null): void {
    this._state.update((state) => ({
      ...state,
      loading: { isLoading, error },
    }));
  }
}
